import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const normalizeOptions = (source) => {
  if (!Array.isArray(source)) return [];

  return source.map((option, index) => {
    if (typeof option === "string") {
      return {
        id: option,
        title: option,
        description: "",
        metadata: "",
        enabled: true,
      };
    }

    return {
      id: option?.id ?? `option_${index + 1}`,
      title: option?.title ?? option?.id ?? `Option ${index + 1}`,
      description: option?.description ?? "",
      metadata: option?.metadata ?? "",
      enabled: option?.enabled !== false,
    };
  });
};

const getInputType = (type) => {
  switch (type) {
    case "number":
      return "number";
    case "email":
      return "email";
    case "phone":
      return "tel";
    case "password":
    case "passcode":
      return "password";
    default:
      return "text";
  }
};

const getReferenceValue = (reference, formData, currentScreenId) => {
  if (typeof reference !== "string") return reference;

  const currentFormMatch = reference.match(/^\$\{form\.([^}]+)\}$/);

  if (currentFormMatch) {
    return formData?.[currentScreenId]?.[currentFormMatch[1]];
  }

  const screenFormMatch = reference.match(
    /^\$\{screen\.([^.]+)\.form\.([^}]+)\}$/
  );

  if (screenFormMatch) {
    return formData?.[screenFormMatch[1]]?.[screenFormMatch[2]];
  }

  return reference;
};

const resolvePayload = (payload, formData, currentScreenId) => {
  if (Array.isArray(payload)) {
    return payload.map((item) =>
      resolvePayload(item, formData, currentScreenId)
    );
  }

  if (payload && typeof payload === "object") {
    return Object.fromEntries(
      Object.entries(payload).map(([key, value]) => [
        key,
        resolvePayload(value, formData, currentScreenId),
      ])
    );
  }

  return getReferenceValue(payload, formData, currentScreenId);
};

const evaluateSimpleCondition = (condition, formData, currentScreenId) => {
  if (typeof condition === "boolean") return condition;

  if (!condition) return false;

  if (typeof condition !== "string") {
    return Boolean(condition);
  }

  const trimmed = condition.trim();

  // Plain dynamic reference:
  // ${form.email}
  // ${screen.CONTACT.form.email}
  const direct = getReferenceValue(trimmed, formData, currentScreenId);

  if (direct !== trimmed) {
    return Boolean(direct);
  }

  // Basic comparisons useful for local preview:
  // ${form.age} >= 18
  // ${form.type} == "business"
  const comparison = trimmed.match(
    /^(.*?)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.*?)$/
  );

  if (!comparison) {
    return Boolean(trimmed);
  }

  const [, leftRaw, operator, rightRaw] = comparison;

  const parseOperand = (raw) => {
    const value = raw.trim();
    const referenced = getReferenceValue(value, formData, currentScreenId);

    if (referenced !== value) {
      return referenced;
    }

    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      return value.slice(1, -1);
    }

    if (value === "true") return true;
    if (value === "false") return false;
    if (value === "null") return null;

    const number = Number(value);
    if (!Number.isNaN(number) && value !== "") return number;

    return value;
  };

  const left = parseOperand(leftRaw);
  const right = parseOperand(rightRaw);

  switch (operator) {
    case "===":
    case "==":
      return left == right; // intentional loose match for preview values
    case "!==":
    case "!=":
      return left != right; // intentional loose match for preview values
    case ">":
      return Number(left) > Number(right);
    case "<":
      return Number(left) < Number(right);
    case ">=":
      return Number(left) >= Number(right);
    case "<=":
      return Number(left) <= Number(right);
    default:
      return false;
  }
};

const renderRichText = (text) => {
  const lines = Array.isArray(text) ? text : String(text ?? "").split("\n");

  return lines.map((line, lineIndex) => {
    const parts = String(line).split(/(\*\*.*?\*\*)/g);

    return (
      <Typography
        key={lineIndex}
        variant="body2"
        component="div"
        sx={{
          minHeight: line === "" ? 10 : "auto",
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
        }}
      >
        {parts.map((part, index) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return (
              <Box key={index} component="span" sx={{ fontWeight: 700 }}>
                {part.slice(2, -2)}
              </Box>
            );
          }

          return <React.Fragment key={index}>{part}</React.Fragment>;
        })}
      </Typography>
    );
  });
};

const WABAFormPreview = ({
  formStructure,
  showDebugData = false,
  onComplete,
  onDataExchange,
}) => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [formData, setFormData] = useState({});
  const [previewMessage, setPreviewMessage] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);

  const screens = useMemo(
    () => (Array.isArray(formStructure?.screens) ? formStructure.screens : []),
    [formStructure?.screens]
  );

  const activeScreen = screens[currentScreen] ?? null;

  useEffect(() => {
    if (screens.length === 0) {
      setCurrentScreen(0);
      return;
    }

    if (currentScreen > screens.length - 1) {
      setCurrentScreen(screens.length - 1);
    }
  }, [screens.length, currentScreen]);

  useEffect(() => {
    setPreviewMessage(null);
  }, [currentScreen]);

  const progress =
    screens.length > 0 ? ((currentScreen + 1) / screens.length) * 100 : 0;

  const handleInputChange = (screenId, field, value) => {
    if (!screenId || !field) return;

    setFormData((prev) => ({
      ...prev,
      [screenId]: {
        ...(prev[screenId] || {}),
        [field]: value,
      },
    }));
  };

  const navigateToScreen = (screenName) => {
    const index = screens.findIndex((screen) => screen.id === screenName);

    if (index === -1) {
      setPreviewMessage({
        severity: "error",
        text: `Target screen "${screenName}" was not found.`,
      });
      return;
    }

    setCurrentScreen(index);
  };

  const validateCurrentScreen = () => {
    if (!activeScreen) return true;

    const children = Array.isArray(activeScreen?.layout?.children)
      ? activeScreen.layout.children
      : [];

    for (const component of children) {
      if (!component?.required || !component?.name) continue;

      const value = formData?.[activeScreen.id]?.[component.name];

      const empty =
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);

      if (empty) {
        setPreviewMessage({
          severity: "warning",
          text: `Please complete "${component.label || component.name}".`,
        });

        return false;
      }

      if (component.type === "CheckboxGroup" && Array.isArray(value)) {
        const min =
          component["min-selected-items"] ?? (component.required ? 1 : 0);

        const max = component["max-selected-items"];

        if (min && value.length < min) {
          setPreviewMessage({
            severity: "warning",
            text: `Select at least ${min} option${min === 1 ? "" : "s"} for "${
              component.label
            }".`,
          });

          return false;
        }

        if (max && value.length > max) {
          setPreviewMessage({
            severity: "warning",
            text: `Select no more than ${max} option${
              max === 1 ? "" : "s"
            } for "${component.label}".`,
          });

          return false;
        }
      }
    }

    return true;
  };

  const handleAction = async (component) => {
    const action = component?.["on-click-action"] || {};
    const actionName = action?.name;

    setPreviewMessage(null);

    if (component?.type === "Footer" && !validateCurrentScreen()) {
      return;
    }

    if (actionName === "navigate") {
      navigateToScreen(action?.next?.name);
      return;
    }

    if (actionName === "open_url") {
      const url = action?.url || component?.url;

      if (!url) {
        setPreviewMessage({
          severity: "error",
          text: "No URL is configured for this link.",
        });
        return;
      }

      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    if (actionName === "data_exchange") {
      const resolvedPayload = resolvePayload(
        action?.payload || {},
        formData,
        activeScreen?.id
      );

      if (typeof onDataExchange === "function") {
        await onDataExchange({
          screen: activeScreen,
          payload: resolvedPayload,
          formData,
          action,
        });
      } else {
        setPreviewMessage({
          severity: "info",
          text: "Preview only: this action would call your configured Flow endpoint.",
        });
      }

      if (action?.next?.name) {
        navigateToScreen(action.next.name);
      }

      return;
    }

    if (actionName === "complete") {
      const resolvedPayload = resolvePayload(
        action?.payload || {},
        formData,
        activeScreen?.id
      );

      setPreviewMessage({
        severity: "success",
        text: "Flow completed successfully in preview.",
      });

      if (typeof onComplete === "function") {
        onComplete({
          payload: resolvedPayload,
          formData,
          screen: activeScreen,
        });
      }

      return;
    }

    // Fallback for older/simple JSON.
    if (component?.type === "Footer") {
      if (currentScreen < screens.length - 1) {
        setCurrentScreen((prev) => prev + 1);
      } else {
        setPreviewMessage({
          severity: "success",
          text: "Flow completed successfully in preview.",
        });

        if (typeof onComplete === "function") {
          onComplete({
            payload: formData,
            formData,
            screen: activeScreen,
          });
        }
      }
    }
  };

  const renderComponent = (component, screenId, keyPrefix = "") => {
    if (!component || typeof component !== "object") return null;

    if (component.visible === false) {
      return null;
    }

    const key = `${keyPrefix}${component.name || component.type}`;

    switch (component.type) {
      case "TextHeading":
        return (
          <Typography
            key={key}
            variant="h5"
            fontWeight={700}
            sx={{ lineHeight: 1.25 }}
          >
            {component.text}
          </Typography>
        );

      case "TextSubheading":
        return (
          <Typography key={key} variant="subtitle1" fontWeight={700}>
            {component.text}
          </Typography>
        );

      case "TextCaption":
        return (
          <Typography
            key={key}
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ lineHeight: 1.45 }}
          >
            {component.text}
          </Typography>
        );

      case "TextBody":
        return (
          <Typography
            key={key}
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}
          >
            {Array.isArray(component.text)
              ? component.text.join("\n")
              : component.text}
          </Typography>
        );

      case "RichText":
        return <Box key={key}>{renderRichText(component.text)}</Box>;

      case "TextInput": {
        const value = formData?.[screenId]?.[component.name] ?? "";

        const minChars = component["min-chars"];
        const maxChars = component["max-chars"];

        return (
          <TextField
            key={key}
            fullWidth
            size="small"
            type={getInputType(component["input-type"])}
            label={component.label}
            required={Boolean(component.required)}
            disabled={component.enabled === false}
            value={value}
            onChange={(event) =>
              handleInputChange(screenId, component.name, event.target.value)
            }
            helperText={
              component["helper-text"] ||
              [
                minChars ? `Min ${minChars}` : "",
                maxChars ? `Max ${maxChars}` : "",
              ]
                .filter(Boolean)
                .join(" · ")
            }
            inputProps={{
              minLength: minChars || undefined,
              maxLength: maxChars || undefined,
              pattern: component.pattern || undefined,
            }}
          />
        );
      }

      case "TextArea": {
        const value = formData?.[screenId]?.[component.name] ?? "";

        return (
          <TextField
            key={key}
            fullWidth
            multiline
            minRows={3}
            maxRows={7}
            size="small"
            label={component.label}
            required={Boolean(component.required)}
            disabled={component.enabled === false}
            value={value}
            onChange={(event) =>
              handleInputChange(screenId, component.name, event.target.value)
            }
            helperText={component["helper-text"] || ""}
            inputProps={{
              maxLength:
                component["max-length"] || component["max-chars"] || undefined,
            }}
          />
        );
      }

      case "DatePicker": {
        const value = formData?.[screenId]?.[component.name] ?? "";

        return (
          <TextField
            key={key}
            fullWidth
            size="small"
            type="date"
            label={component.label}
            required={Boolean(component.required)}
            disabled={component.enabled === false}
            value={value}
            onChange={(event) =>
              handleInputChange(screenId, component.name, event.target.value)
            }
            helperText={component["helper-text"] || ""}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: component["min-date"] || undefined,
              max: component["max-date"] || undefined,
            }}
          />
        );
      }

      case "CalendarPicker": {
        const isRange = component.mode === "range";
        const value =
          formData?.[screenId]?.[component.name] ??
          (isRange ? { start: "", end: "" } : "");

        if (isRange) {
          return (
            <Box key={key}>
              <FormLabel
                required={Boolean(component.required)}
                sx={{ display: "block", mb: 1 }}
              >
                {component.label}
              </FormLabel>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start date"
                  disabled={component.enabled === false}
                  value={value?.start || ""}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: component["min-date"] || undefined,
                    max: component["max-date"] || undefined,
                  }}
                  onChange={(event) =>
                    handleInputChange(screenId, component.name, {
                      ...(value || {}),
                      start: event.target.value,
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End date"
                  disabled={component.enabled === false}
                  value={value?.end || ""}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: value?.start || component["min-date"] || undefined,
                    max: component["max-date"] || undefined,
                  }}
                  onChange={(event) =>
                    handleInputChange(screenId, component.name, {
                      ...(value || {}),
                      end: event.target.value,
                    })
                  }
                />
              </Stack>

              {component["helper-text"] && (
                <FormHelperText>{component["helper-text"]}</FormHelperText>
              )}
            </Box>
          );
        }

        return (
          <TextField
            key={key}
            fullWidth
            size="small"
            type="date"
            label={component.label}
            required={Boolean(component.required)}
            disabled={component.enabled === false}
            value={value}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: component["min-date"] || undefined,
              max: component["max-date"] || undefined,
            }}
            helperText={component["helper-text"] || ""}
            onChange={(event) =>
              handleInputChange(screenId, component.name, event.target.value)
            }
          />
        );
      }

      case "RadioButtonsGroup": {
        const options = normalizeOptions(component["data-source"]);

        return (
          <FormControl
            key={key}
            component="fieldset"
            fullWidth
            required={Boolean(component.required)}
            disabled={component.enabled === false}
          >
            <FormLabel>{component.label}</FormLabel>

            <RadioGroup
              value={formData?.[screenId]?.[component.name] ?? ""}
              onChange={(event) =>
                handleInputChange(screenId, component.name, event.target.value)
              }
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  disabled={!option.enabled}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">{option.title}</Typography>

                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        );
      }

      case "CheckboxGroup": {
        const options = normalizeOptions(component["data-source"]);

        const selected = formData?.[screenId]?.[component.name] || [];

        return (
          <FormControl
            key={key}
            component="fieldset"
            fullWidth
            required={Boolean(component.required)}
            disabled={component.enabled === false}
          >
            <FormLabel>{component.label}</FormLabel>

            <FormGroup>
              {options.map((option) => (
                <FormControlLabel
                  key={option.id}
                  disabled={!option.enabled}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(option.id)}
                      onChange={(event) => {
                        const nextValue = event.target.checked
                          ? [...selected, option.id]
                          : selected.filter((id) => id !== option.id);

                        handleInputChange(screenId, component.name, nextValue);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{option.title}</Typography>

                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </FormGroup>

            {(component["min-selected-items"] ||
              component["max-selected-items"]) && (
              <FormHelperText>
                {component["min-selected-items"]
                  ? `Min ${component["min-selected-items"]}`
                  : ""}
                {component["min-selected-items"] &&
                component["max-selected-items"]
                  ? " · "
                  : ""}
                {component["max-selected-items"]
                  ? `Max ${component["max-selected-items"]}`
                  : ""}
              </FormHelperText>
            )}
          </FormControl>
        );
      }

      case "Dropdown": {
        const options = normalizeOptions(component["data-source"]);

        return (
          <FormControl
            key={key}
            fullWidth
            size="small"
            required={Boolean(component.required)}
            disabled={component.enabled === false}
          >
            <FormLabel sx={{ mb: 0.75 }}>{component.label}</FormLabel>

            <Select
              displayEmpty
              value={formData?.[screenId]?.[component.name] ?? ""}
              onChange={(event) =>
                handleInputChange(screenId, component.name, event.target.value)
              }
            >
              <MenuItem value="" disabled>
                <Typography color="text.secondary">Select</Typography>
              </MenuItem>

              {options.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.id}
                  disabled={!option.enabled}
                >
                  <Box>
                    <Typography variant="body2">{option.title}</Typography>

                    {option.description && (
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      case "OptIn": {
        const checked = Boolean(formData?.[screenId]?.[component.name]);

        return (
          <FormControl key={key} required={Boolean(component.required)}>
            <FormControlLabel
              disabled={component.enabled === false}
              control={
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={(event) =>
                    handleInputChange(
                      screenId,
                      component.name,
                      event.target.checked
                    )
                  }
                />
              }
              label={
                <Typography variant="body2">
                  {component.label || component.text || "I agree"}
                </Typography>
              }
            />
          </FormControl>
        );
      }

      case "EmbeddedLink": {
        const action = component["on-click-action"] || {};
        const isExternal =
          action.name === "open_url" || Boolean(action.url || component.url);

        return (
          <Button
            key={key}
            variant="text"
            size="small"
            startIcon={<LinkOutlinedIcon />}
            endIcon={
              isExternal ? <OpenInNewIcon sx={{ fontSize: 16 }} /> : null
            }
            sx={{
              px: 0.5,
              textTransform: "none",
              justifyContent: "flex-start",
            }}
            onClick={() => handleAction(component)}
          >
            {component.text || component.label || "Learn more"}
          </Button>
        );
      }

      case "Footer":
        return (
          <Button
            key={key}
            variant="contained"
            fullWidth
            disabled={component.enabled === false}
            onClick={() => handleAction(component)}
            sx={{
              mt: 1,
              minHeight: 44,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {component.label || "Continue"}
          </Button>
        );

      case "If": {
        const conditionMet = evaluateSimpleCondition(
          component.condition,
          formData,
          screenId
        );

        const branch = conditionMet ? component.then : component.else;

        return (
          <Stack key={key} spacing={1.5}>
            {(Array.isArray(branch) ? branch : []).map((child, index) =>
              renderComponent(child, screenId, `${keyPrefix}if_${index}_`)
            )}
          </Stack>
        );
      }

      case "Switch": {
        const switchValue = getReferenceValue(
          component.value || component.condition || component.expression,
          formData,
          screenId
        );

        const branch =
          component?.cases?.[switchValue] ||
          component?.cases?.default ||
          component?.default ||
          [];

        return (
          <Stack key={key} spacing={1.5}>
            {(Array.isArray(branch) ? branch : []).map((child, index) =>
              renderComponent(child, screenId, `${keyPrefix}switch_${index}_`)
            )}
          </Stack>
        );
      }

      default:
        return (
          <Alert key={key} severity="warning">
            Preview does not yet render <strong>{component.type}</strong>. The
            component is still preserved in the Flow JSON.
          </Alert>
        );
    }
  };

  if (screens.length === 0) {
    return <Alert severity="info">This Flow has no screens to preview.</Alert>;
  }

  const children = Array.isArray(activeScreen?.layout?.children)
    ? activeScreen.layout.children
    : [];

  return (
    <Box sx={{ width: "100%", maxWidth: 430, mx: "auto" }}>
      <Paper
        elevation={0}
        sx={{
          border: "8px solid #202124",
          borderRadius: "34px",
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0 18px 44px rgba(17,24,39,.16)",
        }}
      >
        {/* Device top */}
        <Box
          sx={{
            height: 24,
            bgcolor: "#202124",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              mt: 0.7,
              width: 68,
              height: 5,
              borderRadius: 10,
              bgcolor: "#5f6368",
            }}
          />
        </Box>

        {/* WhatsApp-style header */}
        <Box
          sx={{
            px: 1.5,
            py: 1.1,
            bgcolor: "#f7f8fa",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              size="small"
              color="inherit"
              disabled={currentScreen === 0}
              onClick={() => setCurrentScreen((prev) => Math.max(0, prev - 1))}
              sx={{
                minWidth: 30,
                width: 30,
                height: 30,
                borderRadius: "50%",
                p: 0,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 19 }} />
            </Button>

            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                bgcolor: "#e8f5e9",
                display: "grid",
                placeItems: "center",
                color: "#137333",
                fontSize: 12,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              WA
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={800} noWrap>
                WhatsApp Flow
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                display="block"
              >
                {activeScreen?.title || activeScreen?.id}
              </Typography>
            </Box>

            <Chip
              size="small"
              label={`${currentScreen + 1}/${screens.length}`}
              variant="outlined"
              sx={{ height: 24 }}
            />
          </Stack>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 3 }}
        />

        {/* Screen selector */}
        <Box sx={{ px: 1.5, pt: 1.25 }}>
          <Select
            fullWidth
            size="small"
            value={currentScreen}
            IconComponent={KeyboardArrowDownIcon}
            onChange={(event) => setCurrentScreen(Number(event.target.value))}
            sx={{
              bgcolor: "#fafafa",
              fontSize: 13,
            }}
          >
            {screens.map((screen, index) => (
              <MenuItem key={screen.id} value={index}>
                {index + 1}. {screen.title || screen.id}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Actual Flow screen */}
        <Box
          sx={{
            px: 2,
            py: 2,
            minHeight: 500,
            maxHeight: "68vh",
            overflowY: "auto",
          }}
        >
          {previewMessage && (
            <Alert
              severity={previewMessage.severity}
              sx={{ mb: 1.5 }}
              onClose={() => setPreviewMessage(null)}
            >
              {previewMessage.text}
            </Alert>
          )}

          {children.length === 0 ? (
            <Box
              sx={{
                minHeight: 320,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                This screen has no components.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.75}>
              {children.map((component, index) => (
                <React.Fragment
                  key={`${component.type}-${component.name || index}`}
                >
                  {renderComponent(component, activeScreen.id, `${index}_`)}
                </React.Fragment>
              ))}
            </Stack>
          )}
        </Box>

        {/* Device bottom */}
        <Box
          sx={{
            height: 24,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              width: 105,
              height: 4,
              borderRadius: 8,
              bgcolor: "#d8dadd",
            }}
          />
        </Box>
      </Paper>

      {showDebugData && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            color="inherit"
            onClick={() => setDebugOpen((prev) => !prev)}
            sx={{ textTransform: "none" }}
          >
            {debugOpen ? "Hide preview data" : "Show preview data"}
          </Button>

          <Collapse in={debugOpen}>
            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#fafafa",
                overflow: "auto",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                display="block"
                mb={1}
              >
                Local preview state
              </Typography>

              <Box
                component="pre"
                sx={{
                  m: 0,
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {JSON.stringify(formData, null, 2)}
              </Box>
            </Paper>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default WABAFormPreview;
